import { Component, OnInit, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  RdsSidenavModule,
  RdsIconsModule,
  RdsButtonModule,
  RdsTooltipModule,
  RdsDividerModule,
  RdsBadgeModule,
} from '@rds/angular-components';
import { UserService } from '../services/user.service';
import { User } from '../models/user.model';
import { DetailedViewObjectService } from '../services/detailed-view-object.service';
import { Activity } from '../models/activity.model';
import { CustomMilestone } from '../models/custom-milestone.model';
import { CreateObject } from '../models/metadata';
import { TimelineDataService, LastSyncData } from '../services/timeline-data.service';
import { FilterService } from '../services/filter.service';
import { DateFormatter } from '../models/date-formatter.model';
import { SyncSource } from '../models/ctmsmilestone.model';
import { RdsToastService } from '@rds/angular-components';
import { Filter } from '../models/filter';
import { FilterPanelService } from '../services/filter-panel.service';
import { SkeletonBlockComponent } from '../shared/skeleton-block/skeleton-block.component';

@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [
    RdsSidenavModule,
    RdsIconsModule,
    RdsButtonModule,
    RdsTooltipModule,
    RdsDividerModule,
    RdsBadgeModule,
    FormsModule,
    SkeletonBlockComponent,
  ],
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
})
export class SidenavComponent implements OnInit, AfterViewInit {
  roleLoaded = false;
  isViewer = true;
  isAdministrator = false;
  isSystemAdmin = false;
  isAdminPanel = false;
  canAccessAuditLogs = false;
  isMinimal = true;

  private readonly AUDIT_LOG_ALLOWED_EMAILS = [
    'piotr.was@roche.com',
    'berk_adrian.greser@roche.com'
  ];
  private readonly SYSTEM_ADMIN_ROLE_ID = 4;

  // Sync state
  isRefreshing = false;
  refreshingSources: Record<SyncSource, boolean> = {
    publications: false,
    ctms: false,
    congress: false,
    veeva: false,
  };
  lastSyncData: { [key in keyof LastSyncData]: string } = {
    lastPublicationSync: '',
    lastMilestoneSync: '',
    lastCongressSync: '',
    lastVVPMSync: '',
  };
  private lastSyncDates: LastSyncData = {
    lastPublicationSync: undefined,
    lastMilestoneSync: undefined,
    lastCongressSync: undefined,
    lastVVPMSync: undefined,
  };
  private static readonly OUTDATED_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24 hours

  // Filter state for gslide export
  private filters!: Filter;
  private exportOptions = {
    type: 'EXPORT_OPTIONS',
    exportActivities: false,
    exportPublications: false,
    exportCtmsMilestones: false,
    exportManualMilestones: false,
  };

  constructor(
    private router: Router,
    private userService: UserService,
    private detailedViewObjectService: DetailedViewObjectService,
    private timelineDataService: TimelineDataService,
    private filterService: FilterService,
    private toastService: RdsToastService,
    private filterPanelService: FilterPanelService,
    private elementRef: ElementRef,
  ) {
    this.timelineDataService.lastSyncData.subscribe((data) => {
      this.lastSyncDates = data;
      this.lastSyncData = {
        lastPublicationSync: new DateFormatter(data.lastPublicationSync!).presentationDate,
        lastMilestoneSync: new DateFormatter(data.lastMilestoneSync!).presentationDate,
        lastCongressSync: new DateFormatter(data.lastCongressSync!).presentationDate,
        lastVVPMSync: new DateFormatter(data.lastVVPMSync!).presentationDate,
      };
    });

    this.filterService.filterData.subscribe((f) => {
      this.filters = f;
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isAdminPanel = this.router.url.includes('/admin');
      }
    });
  }

  ngAfterViewInit() {
    // Allow one frame for the RDS sidenav to settle into its minimal state
    // before enabling transitions, preventing the expand→shrink flash on reload.
    requestAnimationFrame(() => {
      this.elementRef.nativeElement.classList.add('sidenav-ready');
    });
  }

  ngOnInit() {
    this.isAdminPanel = this.router.url.includes('/admin');
    this.userService
      .getUserData()
      .then((user: User) => {
        this.isViewer = user.isViewer;
        this.isAdministrator = user.roleID === 3;
        this.isSystemAdmin = user.roleID === this.SYSTEM_ADMIN_ROLE_ID;
        this.canAccessAuditLogs =
          user.roleID === this.SYSTEM_ADMIN_ROLE_ID ||
          this.AUDIT_LOG_ALLOWED_EMAILS.includes(user.email?.toLowerCase());
        this.roleLoaded = true;
      })
      .catch((error: any) => {
        console.error('Error fetching user data:', error);
        this.roleLoaded = true;
      });
  }

  // --- Action callbacks ---

  createActivity() {
    this.detailedViewObjectService.nextDetailedObject(new Activity());
  }

  createCustomMilestone() {
    this.detailedViewObjectService.nextDetailedObject(new CustomMilestone());
  }

  createGslide() {
    if (this.filters) {
      const exportObject = {
        abstractDate: new Date(),
        abstractID: 'export-options',
        toPost: (data: any) => {
          throw new Error('Export options toPost not implemented');
        },
        type: 'EXPORT_OPTIONS',
        filters: this.filters,
        options: { ...this.exportOptions },
      } as any as CreateObject;

      this.detailedViewObjectService.nextDetailedObject(exportObject);
    } else {
      this.toastService.show(
        'Please apply filters before creating a Google Slide.',
        'Warning',
        { timeOut: 5000 },
      );
    }
  }

  // --- Sync callbacks ---

  sync() {
    this.isRefreshing = true;
    this.timelineDataService.refreshData().then(() => {
      this.isRefreshing = false;
    });
  }

  syncSingle(source: SyncSource) {
    this.refreshingSources[source] = true;
    this.isRefreshing = true;
    this.timelineDataService.refreshSingle(source).then(() => {
      this.refreshingSources[source] = false;
      this.isRefreshing = Object.values(this.refreshingSources).some((v) => v);
    });
  }

  // --- Filters ---

  openFilters() {
    this.filterPanelService.toggle();
  }

  // --- Sync status ---

  isOutdated(key: keyof LastSyncData): boolean {
    const date = this.lastSyncDates[key];
    if (!date) return true;
    return (Date.now() - new Date(date).getTime()) > SidenavComponent.OUTDATED_THRESHOLD_MS;
  }

  outdatedTooltip(key: keyof LastSyncData): string {
    const date = this.lastSyncDates[key];
    if (!date) return 'Never synced — click to sync';
    const diffMs = Date.now() - new Date(date).getTime();
    const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    if (days < 1) return '';
    const label = days === 1 ? '1 day ago' : `${days} days ago`;
    return `Last synced ${label} — click to sync`;
  }

  get anySyncOutdated(): boolean {
    return this.isOutdated('lastPublicationSync')
      || this.isOutdated('lastMilestoneSync')
      || this.isOutdated('lastCongressSync')
      || this.isOutdated('lastVVPMSync');
  }

  private static readonly SYNC_SOURCE_LABELS: Record<keyof LastSyncData, string> = {
    lastPublicationSync: 'Publications',
    lastMilestoneSync: 'Milestones',
    lastCongressSync: 'Congresses',
    lastVVPMSync: 'VVPM',
  };

  get anySyncOutdatedTooltip(): string {
    const keys: (keyof LastSyncData)[] = [
      'lastPublicationSync', 'lastMilestoneSync',
      'lastCongressSync', 'lastVVPMSync',
    ];
    const outdated = keys.filter(k => this.isOutdated(k));
    if (outdated.length === 0) return '';

    // Find the oldest source
    let oldest: keyof LastSyncData = outdated[0];
    for (const k of outdated) {
      const d = this.lastSyncDates[k];
      const o = this.lastSyncDates[oldest];
      if (!d) { oldest = k; break; }
      if (o && new Date(d).getTime() < new Date(o).getTime()) oldest = k;
    }

    const oldestDate = this.lastSyncDates[oldest];
    if (!oldestDate) {
      if (outdated.length === 1) {
        return `${SidenavComponent.SYNC_SOURCE_LABELS[oldest]} data was never synced`;
      }
      return 'Some data was never synced';
    }

    const days = Math.floor((Date.now() - new Date(oldestDate).getTime()) / (24 * 60 * 60 * 1000));
    const age = days === 1 ? '1 day old' : `${days} days old`;

    if (outdated.length === 1) {
      return `${SidenavComponent.SYNC_SOURCE_LABELS[oldest]} data is ${age}`;
    }
    return `Some data is ${age}`;
  }

  // --- Navigation ---

  navigateToAdmin(section?: string) {
    if (section) {
      this.router.navigate(['/admin', section]);
    } else {
      this.router.navigate(['/admin']);
    }
  }

  navigateToRoot() {
    this.router.navigate(['/']);
  }
}
